import * as React from 'react';
import { useState } from 'react';
import styled from 'styled-components';

import { getEnvClassicAssetData } from 'helpers/assets';
import ErrorHandler from 'helpers/error-handler';
import { openCurrentWalletIfExist } from 'helpers/wallet-connect-helpers';

import { useIsMounted } from 'hooks/useIsMounted';

import { LoginTypes } from 'store/authStore/types';
import useAuthStore from 'store/authStore/useAuthStore';

import { BuildSignAndSubmitStatuses } from 'services/auth/wallet-connect/wallet-connect.service';
import { StellarService, ToastService } from 'services/globalServices';

import { ModalProps } from 'types/modal';

import Ice from 'assets/tokens/ice-logo.svg';

import { Button } from 'basics/buttons';
import { ModalDescription, ModalTitle, ModalWrapper, StickyButtonWrapper } from 'basics/ModalAtoms';

import { flexColumn, flexColumnCenter } from 'styles/mixins';
import { COLORS } from 'styles/style-constants';

const AssetBlock = styled.div`
    ${flexColumnCenter};
    padding: 3.7rem 0;
    background-color: ${COLORS.gray50};
    border-radius: 0.5rem;
    margin-bottom: 2.4rem;

    svg {
        height: 3.2rem;
        width: 3.2rem;
        margin-bottom: 2.6rem;
    }

    h4 {
        font-weight: 400;
        font-size: 1.6rem;
        line-height: 2.8rem;
        color: ${COLORS.textPrimary};
    }

    span {
        color: ${COLORS.textGray};
    }
`;

const Buttons = styled(StickyButtonWrapper)`
    ${flexColumn};
    gap: 1.6rem;
`;

const RemoveDownvoteTrustlineModal = ({ close }: ModalProps<never>) => {
    const { account } = useAuthStore();
    const [pending, setPending] = useState(false);

    const isMounted = useIsMounted();

    const onRemove = async () => {
        if (account.authType === LoginTypes.walletConnect) {
            openCurrentWalletIfExist();
        }
        setPending(true);
        try {
            const op = StellarService.op.createRemoveTrustOperation(
                getEnvClassicAssetData('downvoteIce').asset,
            );

            const tx = await StellarService.tx.buildTx(account, op);

            const result = await account.signAndSubmitTx(tx);

            if (
                (result as { status: BuildSignAndSubmitStatuses })?.status ===
                BuildSignAndSubmitStatuses.pending
            ) {
                ToastService.showSuccessToast(
                    'More signatures are required to complete the transaction.',
                );
                if (isMounted.current) {
                    setPending(false);
                }
                return;
            }

            ToastService.showSuccessToast('Trustline removed successfully');
            close();
        } catch (e) {
            const errorText = ErrorHandler(e);
            ToastService.showErrorToast(errorText);
            if (isMounted.current) {
                setPending(false);
            }
        }
    };

    return (
        <ModalWrapper>
            <ModalTitle>Unused Trustline</ModalTitle>
            <ModalDescription>
                downvoteICE is no longer used — voting against markets has been discontinued. You
                can safely remove its trustline. Doing so frees up the 0.5 XLM base reserve
                currently held on your account for this asset.
            </ModalDescription>
            <AssetBlock>
                <Ice />
                <h4>{getEnvClassicAssetData('downvoteIce').code}</h4>
                <span>aqua.network</span>
            </AssetBlock>
            <Buttons>
                <Button isBig fullWidth pending={pending} onClick={() => onRemove()}>
                    remove trustline
                </Button>
            </Buttons>
        </ModalWrapper>
    );
};

export default RemoveDownvoteTrustlineModal;
